import { DomainError, newId, toLocal } from '@nandanam/domain';
import { materialize, emptyState, orderOps, type Materialized, type State } from './materialize';
import { RemoteError, type AccessLevel, type Op, type Remote } from './types';

/** Device-local persistence (IndexedDB in the apps). Always keyed by signed-in account and business folder. */
export interface LocalStore {
  load(key: string): Promise<unknown | null>;
  save(key: string, value: unknown): Promise<void>;
  remove(key: string): Promise<void>;
}

export class MemoryLocalStore implements LocalStore {
  data = new Map<string, string>();
  async load(k: string) { const v = this.data.get(k); return v ? JSON.parse(v) : null; }
  async save(k: string, v: unknown) { this.data.set(k, JSON.stringify(v)); }
  async remove(k: string) { this.data.delete(k); }
}

export type SyncStatus = 'loading' | 'saving' | 'saved' | 'pending' | 'offline' | 'error' | 'denied';

interface Persisted {
  v: 1;
  remote: { op: Op; createdTime: string; fileId: string }[];
  outbox: { op: Op; lastError: string | null }[];
  lastSyncAt: string | null;
  access: AccessLevel | null;
}

/** Only these may be created without a connection; they upload later as drafts. */
export function allowedOffline(cmd: string, args: any): boolean {
  return cmd === 'customer.create' || (cmd === 'record.create' && args?.status === 'enquiry') || cmd === 'record.notes';
}

export interface EngineOptions {
  remote: Remote;
  local: LocalStore;
  accountId: string;
  deviceId: string;
  actor: string;
  now?: () => number;
  numberStart?: number;
  initial?: State;
}

export class SyncError extends Error {
  constructor(public kind: 'offline' | 'view-only' | 'denied' | 'rejected', message: string, public cause?: unknown) {
    super(message);
    this.name = 'SyncError';
  }
}

/**
 * One device's view of the business. All changes go through `act` (or `draft` for offline-allowed
 * ones). The engine never reports a change as saved until storage has acknowledged its op file, and it
 * never hides an op that failed to apply.
 */
export class Engine {
  private remoteOps = new Map<string, { op: Op; createdTime: string; fileId: string }>();
  private outbox: { op: Op; lastError: string | null }[] = [];
  view!: Materialized;
  status: SyncStatus = 'loading';
  access: AccessLevel | null = null;
  lastSyncAt: string | null = null;
  lastError: string | null = null;
  private listeners = new Set<() => void>();
  private now: () => number;
  private key: string;

  constructor(private o: EngineOptions) {
    this.now = o.now ?? Date.now;
    this.key = `nandanam:${o.accountId}`;
    this.rebuild();
  }

  subscribe(f: () => void) { this.listeners.add(f); return () => { this.listeners.delete(f); }; }
  private emit() { for (const f of this.listeners) f(); }

  get state(): State { return this.view.state; }
  get pending(): readonly { op: Op; lastError: string | null }[] { return this.outbox; }
  isPending(opId: string) { return this.outbox.some(x => x.op.opId === opId); }
  rejectedOp(opId: string) { return this.view.rejected.find(r => r.op.opId === opId) ?? null; }

  /** Load this account's cached copy (never another account's). */
  async open(): Promise<void> {
    const p = (await this.o.local.load(this.key)) as Persisted | null;
    if (p?.v === 1) {
      for (const r of p.remote) this.remoteOps.set(r.op.opId, r);
      this.outbox = p.outbox;
      this.lastSyncAt = p.lastSyncAt;
      this.access = p.access;
    }
    this.rebuild();
    this.status = this.outbox.length ? 'pending' : 'loading';
    this.emit();
  }

  private async persist() {
    const p: Persisted = { v: 1, remote: [...this.remoteOps.values()], outbox: this.outbox, lastSyncAt: this.lastSyncAt, access: this.access };
    await this.o.local.save(this.key, p);
  }

  private rebuild() {
    const ops = [
      ...[...this.remoteOps.values()].map(r => ({ op: r.op, createdTime: r.createdTime })),
      ...this.outbox.filter(x => !this.remoteOps.has(x.op.opId)).map(x => ({ op: x.op, createdTime: null })),
    ];
    const latest = [...this.remoteOps.values()].reduce<string | null>((t, r) => (!t || r.createdTime > t ? r.createdTime : t), null);
    this.view = materialize(ops, { now: this.now(), numberStart: this.o.numberStart ?? 1001, latestServerTime: latest, initial: this.o.initial ?? emptyState() });
  }

  private fail(e: unknown): never {
    if (e instanceof RemoteError) {
      if (e.kind === 'offline') { this.status = 'offline'; this.lastError = e.message; }
      else if (e.kind === 'denied' || e.kind === 'not-found') { this.status = 'denied'; this.access = 'none'; this.lastError = e.message; }
      else { this.status = 'error'; this.lastError = e.message; }
    }
    this.emit();
    throw e;
  }

  /** Download new ops and rebuild. */
  async pull(): Promise<void> {
    try {
      this.access = await this.o.remote.access();
      const list = await this.o.remote.listOps();
      // Read new op files a few at a time: a new device may have thousands to fetch.
      const todo = list.filter(m => !this.remoteOps.has(m.opId));
      let failed: unknown = null;
      const worker = async () => {
        for (let m = todo.shift(); m && !failed; m = todo.shift()) {
          try {
            const op = await this.o.remote.readOp(m);
            this.remoteOps.set(op.opId, { op, createdTime: m.createdTime, fileId: m.fileId });
          } catch (e) { failed ??= e; }
        }
      };
      await Promise.all(Array.from({ length: 6 }, worker));
      if (failed) throw failed;
      this.outbox = this.outbox.filter(x => !this.remoteOps.has(x.op.opId));
      this.lastSyncAt = toLocal(this.now());
      this.lastError = null;
      this.rebuild();
      await this.persist();
      this.status = this.outbox.length ? 'pending' : 'saved';
      this.emit();
    } catch (e) { await this.persist(); this.fail(e); }
  }

  private pushing: Promise<void> | null = null;

  /**
   * Upload queued ops in order. Retries reuse the same opId, so nothing is created twice. Only one upload
   * runs at a time: a second caller waits for the running one, which also takes ops queued meanwhile.
   */
  push(): Promise<void> {
    this.pushing ??= this.pushAll().finally(() => { this.pushing = null; });
    return this.pushing;
  }

  private async pushAll(): Promise<void> {
    if (!this.outbox.length) return;
    this.status = 'saving';
    this.emit();
    for (;;) {
      this.outbox = this.outbox.filter(x => !this.remoteOps.has(x.op.opId));
      const item = this.outbox[0];
      if (!item) break;
      try {
        const meta = await this.o.remote.createOp(item.op);
        this.remoteOps.set(item.op.opId, { op: item.op, createdTime: meta.createdTime, fileId: meta.fileId });
        this.outbox = this.outbox.filter(x => x.op.opId !== item.op.opId);
        await this.persist();
      } catch (e) {
        item.lastError = e instanceof Error ? e.message : String(e);
        await this.persist();
        this.rebuild();
        this.fail(e);
      }
    }
    this.rebuild();
    this.status = 'saved';
    this.emit();
  }

  async sync(): Promise<void> { await this.push(); await this.pull(); }

  private build(cmd: string, entity: string, args: any): Op {
    const opId = newId(this.now());
    return { v: 1, opId, deviceId: this.o.deviceId, actor: this.o.actor, at: toLocal(this.now()), entity, basis: this.view.heads.get(entity) ?? null, cmd, args };
  }

  /** Validate an op against current local state without keeping it. */
  private preview(op: Op) {
    const saved = this.outbox;
    this.outbox = [...saved, { op, lastError: null }];
    this.rebuild();
    const rej = this.view.rejected.find(r => r.op.opId === op.opId);
    this.outbox = saved;
    this.rebuild();
    if (rej) throw new DomainError(rej.code as any, rej.reason);
  }

  private queue(op: Op) {
    this.outbox.push({ op, lastError: null });
    this.rebuild();
    this.status = 'pending';
    this.emit();
  }

  /**
   * A consequential change (hold, confirmation, payment, refund, settings…). Needs a connection:
   * pulls first so the check runs on current data, uploads, then pulls again and reports whether the
   * change survived against everything else in storage.
   */
  async act(cmd: string, entity: string, args: any): Promise<{ opId: string; verified: boolean; rejected: string | null }> {
    if (this.access === 'viewer') throw new SyncError('view-only', 'You have view-only access to this business folder');
    if (this.access === 'none') throw new SyncError('denied', 'This account has no access to the business folder');
    try { await this.pull(); } catch (e) {
      if (e instanceof RemoteError && e.kind === 'offline') throw new SyncError('offline', 'You are offline. This change needs a connection.', e);
      throw e;
    }
    if (this.access !== 'editor') throw new SyncError('view-only', 'You have view-only access to this business folder');
    const op = this.build(cmd, entity, args);
    this.preview(op);
    this.queue(op);
    await this.persist();
    try { await this.push(); } catch (e) {
      // Refused by the folder's sharing (for example view-only): this change was never saved, so it must not
      // wait in the outbox as if it would upload later.
      if (e instanceof RemoteError && e.kind === 'denied') {
        this.outbox = this.outbox.filter(x => x.op.opId !== op.opId);
        this.rebuild();
        await this.persist();
        this.emit();
      }
      throw e;
    }
    await this.pull();
    const rej = this.rejectedOp(op.opId);
    return { opId: op.opId, verified: !rej && !this.isPending(op.opId), rejected: rej?.reason ?? null };
  }

  /**
   * Saves a change on this device straight away and uploads it in the background, so the person is not
   * kept waiting. It is checked against the cached copy now, and against OneDrive once uploaded:
   * `done` settles with the outcome. A change that needs a connection and cannot be uploaded is taken
   * back off this device (rejected `done`), so it is never shown as saved when it is not. Offline-capable
   * changes (enquiry drafts, notes) stay queued instead and upload later.
   */
  async submit(cmd: string, entity: string, args: any): Promise<{ opId: string; done: Promise<{ verified: boolean; rejected: string | null }> }> {
    if (this.access === 'viewer') throw new SyncError('view-only', 'You have view-only access to this business folder');
    if (this.access === 'none') throw new SyncError('denied', 'This account has no access to the business folder');
    const offlineOk = allowedOffline(cmd, args);
    const op = this.build(cmd, entity, args);
    this.preview(op);
    this.queue(op);
    await this.persist();
    const done = (async () => {
      try {
        await this.push();
        await this.pull();
      } catch (e) {
        const stays = offlineOk && e instanceof RemoteError && e.kind !== 'denied';
        if (!stays && this.isPending(op.opId)) {
          this.outbox = this.outbox.filter(x => x.op.opId !== op.opId);
          this.rebuild();
          await this.persist();
          this.status = this.outbox.length ? 'pending' : this.status;
          this.emit();
        }
        if (stays) return { verified: false, rejected: null };
        throw e;
      }
      const rej = this.rejectedOp(op.opId);
      return { verified: !rej && !this.isPending(op.opId), rejected: rej?.reason ?? null };
    })();
    done.catch(() => { /* the caller reports it */ });
    return { opId: op.opId, done };
  }

  /** Offline-capable change (new enquiry draft, notes). Saved locally first; uploaded when possible. */
  async draft(cmd: string, entity: string, args: any): Promise<{ opId: string; uploaded: boolean }> {
    if (!allowedOffline(cmd, args)) throw new SyncError('offline', 'This change needs a connection');
    if (this.access === 'viewer') throw new SyncError('view-only', 'You have view-only access to this business folder');
    const op = this.build(cmd, entity, args);
    this.preview(op);
    this.queue(op);
    await this.persist();
    try { await this.sync(); } catch (e) { if (!(e instanceof RemoteError)) throw e; }
    return { opId: op.opId, uploaded: !this.isPending(op.opId) };
  }

  /** Forget this account's cache on this device (sign-out with "remove data from this device"). */
  async forget(): Promise<void> {
    await this.o.local.remove(this.key);
    this.remoteOps.clear();
    this.outbox = [];
    this.rebuild();
  }

  newEntityId(): string { return newId(this.now()); }

  /** Every op this device knows, in replay order (for backups and the review screen). */
  log(): { op: Op; createdTime: string | null }[] {
    return orderOps([
      ...[...this.remoteOps.values()].map(r => ({ op: r.op, createdTime: r.createdTime as string | null })),
      ...this.outbox.filter(x => !this.remoteOps.has(x.op.opId)).map(x => ({ op: x.op, createdTime: null })),
    ]);
  }
}
